import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { PMSimpleSelectorFieldV2 } from "../PMSimpleSelector/PMSimpleSelectorFieldV2";
import { fetchProducts } from "../../../api/product";
import { fetchVehicles } from "../../../api/vehicle";
import { CustomerSelector } from "./customerSelector";
import { HseDocumentSelector } from "./hseDocumentSelector";
import { ProjectSelector } from "./projectSelector";
import { ReportSelector } from "./reportSelector";

function FaqVehiclePicker({ name, index, row, formik }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({
    id: null,
    field1: t("resource_hub.faq.linked_records_pick"),
    field2: null,
  });

  useEffect(() => {
    const vid = row.linkable_id;
    if (vid !== "" && vid != null) {
      setSelected({
        id: Number(vid),
        field1: row._title || `#${vid}`,
        field2: null,
      });
    } else {
      setSelected({
        id: null,
        field1: t("resource_hub.faq.linked_records_pick"),
        field2: null,
      });
    }
  }, [row.linkable_id, row._title, t]);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await fetchVehicles({
        query: q && String(q).trim() ? String(q).trim() : undefined,
        page: 1,
        per_page: 40,
        sortBy: "created_at",
        sort: "DESC",
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setItems(
        rows.map((v) => ({
          value: v.id,
          id: v.id,
          primary:
            v.displayTitle ||
            v.nickname ||
            v.number_plate ||
            `#${v.id}`,
          secondary:
            [v.make, v.model, v.number_plate].filter(Boolean).join(" · ") ||
            null,
          object: v,
        }))
      );
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useMemo(() => debounce((q) => load(q), 400), [load]);

  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);

  const handleChange = (data) => {
    const item = data?.selected?.item;
    if (item == null || item.id == null) return;
    setSelected({
      id: item.id,
      field1: item.primary,
      field2: item.secondary,
    });
    setOpen(false);
    formik.setFieldValue(name, item.id);
    formik.setFieldValue(`linked_records.${index}._title`, item.primary);
  };

  const labelAction = { action: () => {} };

  return (
    <PMSimpleSelectorFieldV2
      errors={formik.errors}
      name={name}
      loading={loading}
      label="resource_hub.faq.link_type.vehicle"
      labelAction={labelAction}
      options={items}
      selected={selected}
      onChange={handleChange}
      onQueryChange={(q) => setQuery(q)}
      onClose={() => setOpen((o) => !o)}
      open={open}
      listSecondaryLimit={40}
    />
  );
}

function FaqProductPicker({ name, index, row, formik }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({
    id: null,
    field1: t("resource_hub.faq.linked_records_pick"),
    field2: null,
  });

  useEffect(() => {
    const pid = row.linkable_id;
    if (pid !== "" && pid != null) {
      setSelected({
        id: Number(pid),
        field1: row._title || `#${pid}`,
        field2: null,
      });
    } else {
      setSelected({
        id: null,
        field1: t("resource_hub.faq.linked_records_pick"),
        field2: null,
      });
    }
  }, [row.linkable_id, row._title, t]);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await fetchProducts({
        query: q && String(q).trim() ? String(q).trim() : undefined,
        page: 1,
        per_page: 40,
        sortBy: "updated_at",
        sort: "desc",
        fields: "id,displayTitle,item_number",
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setItems(
        rows.map((p) => ({
          value: p.id,
          id: p.id,
          primary: p.displayTitle || p.name || `#${p.id}`,
          secondary: p.item_number || null,
          object: p,
        }))
      );
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useMemo(() => debounce((q) => load(q), 400), [load]);

  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);

  const handleChange = (data) => {
    const item = data?.selected?.item;
    if (item == null || item.id == null) return;
    setSelected({
      id: item.id,
      field1: item.primary,
      field2: item.secondary,
    });
    setOpen(false);
    formik.setFieldValue(name, item.id);
    formik.setFieldValue(`linked_records.${index}._title`, item.primary);
  };

  const labelAction = { action: () => {} };

  return (
    <PMSimpleSelectorFieldV2
      errors={formik.errors}
      name={name}
      loading={loading}
      label="resource_hub.faq.link_type.product"
      labelAction={labelAction}
      options={items}
      selected={selected}
      onChange={handleChange}
      onQueryChange={(q) => setQuery(q)}
      onClose={() => setOpen((o) => !o)}
      open={open}
      listSecondaryLimit={40}
    />
  );
}

/**
 * Searchable record picker for FAQ “linked resources” rows.
 * Writes `linkable_id` (and display label in `_title`) for the active `linkable_type`.
 */
export function FaqLinkedResourcePicker({ index, row, formik }) {
  const type = row.linkable_type;
  const name = `linked_records.${index}.linkable_id`;
  const sid =
    row.linkable_id !== "" && row.linkable_id != null
      ? Number(row.linkable_id)
      : null;

  if (!type) {
    return null;
  }

  if (type === "Hse::Document") {
    return (
      <HseDocumentSelector
        name={name}
        label="resource_hub.faq.link_type.hse_document"
        selected={
          sid != null && !Number.isNaN(sid)
            ? { id: sid, displayTitle: row._title }
            : null
        }
        setFieldValue={formik.setFieldValue}
        values={formik.values}
        errors={formik.errors}
        onChange={(e) => {
          const doc = e.target?.hse_document;
          const title = doc?.title || e.target?.data?.primary;
          if (title != null)
            formik.setFieldValue(`linked_records.${index}._title`, title);
        }}
      />
    );
  }

  if (type === "Vehicle") {
    return <FaqVehiclePicker name={name} index={index} row={row} formik={formik} />;
  }

  if (type === "Product") {
    return <FaqProductPicker name={name} index={index} row={row} formik={formik} />;
  }

  if (type === "Report") {
    return (
      <ReportSelector
        name={name}
        label="resource_hub.faq.link_type.report"
        selected={
          sid != null && !Number.isNaN(sid)
            ? { id: sid, displayTitle: row._title }
            : null
        }
        setFieldValue={formik.setFieldValue}
        values={formik.values}
        errors={formik.errors}
        onChange={(e) => {
          const r = e.target?.report || e.target?.data?.object;
          const title =
            r?.displayTitle ||
            e.target?.data?.primary ||
            (sid != null ? String(sid) : "");
          if (title) formik.setFieldValue(`linked_records.${index}._title`, title);
        }}
      />
    );
  }

  if (type === "Customer") {
    return (
      <CustomerSelector
        name={name}
        label="resource_hub.faq.link_type.customer"
        selected={
          sid != null && !Number.isNaN(sid)
            ? { id: sid, displayTitle: row._title }
            : null
        }
        setFieldValue={formik.setFieldValue}
        values={formik.values}
        errors={formik.errors}
        onChange={(e) => {
          const c = e.target?.data;
          const title =
            c?.displayTitle || c?.name || (sid != null ? String(sid) : "");
          if (title != null)
            formik.setFieldValue(`linked_records.${index}._title`, title);
        }}
      />
    );
  }

  if (type === "Project") {
    return (
      <ProjectSelector
        name={name}
        label="resource_hub.faq.link_type.project"
        selected={
          sid != null && !Number.isNaN(sid)
            ? {
                id: sid,
                displayTitle: row._title,
              }
            : null
        }
        setFieldValue={formik.setFieldValue}
        errors={formik.errors}
        onChange={(e) => {
          const o = e.target?.object;
          const title =
            o?.displayTitle ||
            o?.name ||
            e.target?.data?.primary ||
            (sid != null ? String(sid) : "");
          if (title)
            formik.setFieldValue(`linked_records.${index}._title`, title);
        }}
      />
    );
  }

  return null;
}
